import { Injectable, inject, signal } from '@angular/core';
import { Observable, finalize, from, map, of, switchMap, throwError } from 'rxjs';
import { AuthService } from '@app/auth/services/auth.service';
import { mapAuthSelectTenantResponse } from '@app/auth/models/auth-select-tenant-response.model';
import type { AuthSelectTenantResponse } from '@app/auth/models/auth-select-tenant-response.model';
import { RealtimeHubService } from '../hubs/realtime/realtime-hub.service';
import { AuthTokenService } from './auth-token.service';
import { SidebarModulesService } from './sidebar-modules.service';
import { UserSessionService } from './user-session.service';

@Injectable({ providedIn: 'root' })
export class TenantSwitchService {
  private readonly auth = inject(AuthService);
  private readonly tokens = inject(AuthTokenService);
  private readonly session = inject(UserSessionService);
  private readonly realtimeHub = inject(RealtimeHubService);
  private readonly sidebarModules = inject(SidebarModulesService);

  readonly isSwitching = signal(false);

  switchTenant(tenantId: string): Observable<void> {
    const normalizedTenantId = tenantId.trim();
    const user = this.session.user();
    const userId = user?.id?.trim();
    const currentTenantId = user?.tenantId?.trim();

    if (!normalizedTenantId || !userId) {
      return throwError(() => new Error('Tenant switch requires an authenticated user.'));
    }

    if (normalizedTenantId === currentTenantId) {
      return of(void 0);
    }

    const remember = this.tokens.token()?.remember ?? true;
    this.isSwitching.set(true);

    return this.auth
      .selectTenant({
        userId,
        tenantId: normalizedTenantId,
        rememberMe: remember,
      })
      .pipe(
        switchMap((res) =>
          from(this.applySessionAndReconnect(res, remember)).pipe(map(() => void 0)),
        ),
        finalize(() => this.isSwitching.set(false)),
      );
  }

  /**
   * Re-calls select-tenant for the *current* tenant to refresh session data
   * (e.g. currency) that changed server-side without switching tenants.
   * Unlike `switchTenant`, this never short-circuits on an unchanged tenantId.
   */
  refreshCurrentTenant(): Observable<void> {
    const user = this.session.user();
    const userId = user?.id?.trim();
    const tenantId = user?.tenantId?.trim();

    if (!userId || !tenantId) {
      return throwError(() => new Error('Refreshing the session requires an authenticated user.'));
    }

    const remember = this.tokens.token()?.remember ?? true;

    return this.auth.selectTenant({ userId, tenantId, rememberMe: remember }).pipe(
      switchMap((res) =>
        from(this.applySessionAndReconnect(res, remember)).pipe(map(() => void 0)),
      ),
    );
  }

  private async applySessionAndReconnect(
    res: AuthSelectTenantResponse,
    remember: boolean,
  ): Promise<void> {
    const { token, user, tenants } = mapAuthSelectTenantResponse(res, remember);

    if (!token.accessToken.trim()) {
      throw new Error('Tenant switch returned an empty access token.');
    }

    this.tokens.setToken(token, { remember });
    this.session.setUser(user, { remember });
    this.session.setTenants(tenants, { remember });

    await this.realtimeHub.disconnect();
    await this.realtimeHub.connect();
    this.sidebarModules.ensureLoaded({ force: true }).subscribe();
  }
}
